import { useRef, useCallback, MutableRefObject } from 'react';
import EventsService from '@services/EventsService';
import ParticipantsService from '@services/ParticipantsService';
import { ProgressEntry } from '@interfaces/assistantScript';
import {
  ProgressEnvelope,
  parseProgressEnvelope,
  getProgressBrandingKey,
  encodeProgressForBranding,
} from '@shared/utils/assistantProgress';
import { LinearScriptPlayer } from './scriptPlayer/linearScriptPlayer';
import { debugEventId } from '@core/assistant/constants';
import { log } from './Debug';
import { AWAITING_RESPONSE_TYPE } from '@core/assistant/interfaces';

export type UseAssistantProgressParams = {
  mytaverseEventRef: MutableRefObject<any>;
  linearScriptPlayerRef: MutableRefObject<LinearScriptPlayer | undefined>;
  stopPlaybackRef: MutableRefObject<() => void>;
  enqueuedScriptIdRef: MutableRefObject<string | null>;
  resetAssistantUIRef: MutableRefObject<() => void>;
  setAwaitingResponseType: (type: AWAITING_RESPONSE_TYPE) => void;
  activeBrandingIdRef: MutableRefObject<string | undefined>;
};

export const useAssistantProgress = ({
  mytaverseEventRef,
  linearScriptPlayerRef,
  stopPlaybackRef,
  enqueuedScriptIdRef,
  resetAssistantUIRef,
  setAwaitingResponseType,
  activeBrandingIdRef,
}: UseAssistantProgressParams) => {
  const progressState = useRef('{}');
  const saveProgressQueueRef = useRef<Promise<void>>(Promise.resolve());
  const sessionProgressRef = useRef<Record<string, ProgressEntry[]>>({});
  // Full multi-branding envelope as last known from the server, so a save of
  // the current branding's flat bucket doesn't clobber other brandings' progress.
  const rawEnvelopeRef = useRef<ProgressEnvelope>({});

  // Progress is scoped by the room's live branding, falling back to whatever
  // branding the assistant module itself resolved, then to "no branding" —
  // same precedence useAssistantScripts.ts uses to filter scripts.
  const getCurrentBrandingKey = () =>
    getProgressBrandingKey(
      mytaverseEventRef.current?.currentRoom?.activeBrandingId ||
        activeBrandingIdRef.current,
    );

  const mergeProgressStates = (
    current: string | null | undefined,
    stored: string | null | undefined,
  ): string => {
    const currentState: Record<string, ProgressEntry[]> = JSON.parse(
      current || '{}',
    );
    const storedState: Record<string, ProgressEntry[]> = JSON.parse(
      stored || '{}',
    );

    const merged = { ...storedState };
    for (const [key, entries] of Object.entries(currentState)) {
      if (!merged[key]) {
        merged[key] = entries;
      } else {
        for (const entry of entries) {
          const exists = merged[key].some((e) => e[0] === entry[0]);
          if (!exists) {
            merged[key].push(entry);
          }
        }
        merged[key].sort((a, b) => a[0] - b[0]);
      }
    }
    return JSON.stringify(merged);
  };

  const enqueueProgressSave = useCallback(() => {
    const stateAtEnqueue = progressState.current;
    const brandingKeyAtEnqueue = getCurrentBrandingKey();
    log(
      '[progress] enqueueProgressSave called, state at enqueue:',
      stateAtEnqueue,
    );
    saveProgressQueueRef.current = saveProgressQueueRef.current.then(
      async () => {
        log(
          '[progress] queue item executing, state at execute time:',
          progressState.current,
        );
        if (mytaverseEventRef.current?.currentEventId || debugEventId) {
          const envelopeToSend = encodeProgressForBranding(
            rawEnvelopeRef.current,
            brandingKeyAtEnqueue,
            JSON.parse(stateAtEnqueue),
          );
          rawEnvelopeRef.current = parseProgressEnvelope(envelopeToSend);
          await EventsService.setEventUserData(
            mytaverseEventRef.current?.currentEventId ?? debugEventId,
            { assistantState: envelopeToSend },
          );
          log('[progress] server save done, saved:', envelopeToSend);
        } else {
          log('[progress] SKIPPED server save — no eventId');
        }
      },
    );
  }, []);

  const cbOnCompletedStep = useCallback(
    (trigger: string, stepNumber: number, step: string) => {
      log(
        `[progress] cbOnCompletedStep called — trigger=${trigger} stepNumber=${stepNumber} step=${step} state BEFORE:`,
        progressState.current,
      );
      log(`${trigger} step ${step} completed`, {
        progressState: progressState.current,
        trigger,
      });

      const currentProgressState: Record<string, ProgressEntry[]> = JSON.parse(
        progressState.current || '{}',
      );

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      const existingIndex = currentProgressState[trigger].findIndex(
        (e) => e[0] === stepNumber,
      );
      if (existingIndex === -1) {
        currentProgressState[trigger].push([stepNumber, Date.now()]);
      } else {
        currentProgressState[trigger][existingIndex][1] = Date.now();
      }

      progressState.current = JSON.stringify(currentProgressState);

      if (!sessionProgressRef.current[trigger]) {
        sessionProgressRef.current[trigger] = [];
      }
      const sessionExistingIndex = sessionProgressRef.current[
        trigger
      ].findIndex((e) => e[0] === stepNumber);
      if (sessionExistingIndex === -1) {
        sessionProgressRef.current[trigger].push([stepNumber, Date.now()]);
      } else {
        sessionProgressRef.current[trigger][sessionExistingIndex][1] =
          Date.now();
      }
      log(
        `[progress] cbOnCompletedStep state AFTER write:`,
        progressState.current,
      );

      log(
        'assistant progress saved',
        JSON.stringify(currentProgressState, null, 2),
      );

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const saveQuizAnswers = useCallback(
    async (selectedOptions: number[]) => {
      const player = linearScriptPlayerRef.current;
      const trigger = player?.getCurrentState().name;
      const nodeId = player?.getCurrentNodeId();

      log('saveQuizAnswers input', {
        trigger,
        nodeId,
        progressState: progressState.current,
      });

      if (!trigger || !nodeId) {
        log('saveQuizAnswers: no trigger or nodeId');
        return;
      }

      const stepNumber = parseInt(nodeId.replace('step-', ''), 10) - 1;
      if (isNaN(stepNumber)) {
        log('saveQuizAnswers: invalid stepNumber from nodeId', nodeId);
        return;
      }

      const currentProgressState: Record<string, ProgressEntry[]> = JSON.parse(
        progressState.current || '{}',
      );

      if (!currentProgressState[trigger]) {
        currentProgressState[trigger] = [];
      }
      const entries = currentProgressState[trigger];

      const entryIndex = entries.findIndex((e) => e[0] === stepNumber);

      if (entryIndex !== -1) {
        entries[entryIndex][2] = selectedOptions;
      } else {
        entries.push([stepNumber, Date.now(), selectedOptions]);
      }
      progressState.current = JSON.stringify(currentProgressState);

      log('quiz answers saved', JSON.stringify(currentProgressState, null, 2));

      enqueueProgressSave();
    },
    [enqueueProgressSave],
  );

  const applyServerEnvelope = (raw: string | null | undefined) => {
    const brandingKey = getCurrentBrandingKey();
    const decodedEnvelope = parseProgressEnvelope(raw);
    progressState.current = mergeProgressStates(
      progressState.current,
      JSON.stringify(decodedEnvelope[brandingKey] ?? {}),
    );
    rawEnvelopeRef.current = {
      ...decodedEnvelope,
      [brandingKey]: JSON.parse(progressState.current),
    };
  };

  const fetchParticipantProgress = useCallback(async () => {
    log(
      '[progress] fetchParticipantProgress called, current state BEFORE:',
      progressState.current,
    );
    if (!mytaverseEventRef.current?.currentEventId) {
      const allParticipantEvents =
        await ParticipantsService.getCurrentParticipantEvents();
      const eventParticipantData = allParticipantEvents.events.find(
        (event: any) => event.eventId === debugEventId,
      );

      if (eventParticipantData) {
        log(
          '[progress] fetchParticipantProgress server assistantState:',
          eventParticipantData?.assistantState,
        );
        applyServerEnvelope(eventParticipantData?.assistantState as string);
      } else {
        log('no debug state data');
      }
    } else {
      log(
        '[progress] fetchParticipantProgress server assistantState:',
        mytaverseEventRef.current.currentEventParticipant?.assistantState,
      );
      applyServerEnvelope(
        mytaverseEventRef.current.currentEventParticipant
          ?.assistantState as string,
      );
    }

    log(
      '[progress] fetchParticipantProgress state AFTER merge:',
      progressState.current,
    );
    log('loaded participant progress', progressState.current);
  }, []);

  const resetProgress = useCallback(async () => {
    log('[progress] resetProgress called');
    if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
      linearScriptPlayerRef.current.stop();
    }
    stopPlaybackRef.current();
    enqueuedScriptIdRef.current = null;
    resetAssistantUIRef.current();
    setAwaitingResponseType(AWAITING_RESPONSE_TYPE.NONE);
    progressState.current = '{}';
    sessionProgressRef.current = {};

    // Scoped to the current branding only — other brandings' recorded
    // progress is untouched.
    const brandingKey = getCurrentBrandingKey();
    const envelopeAfterReset = encodeProgressForBranding(
      rawEnvelopeRef.current,
      brandingKey,
      {},
    );
    rawEnvelopeRef.current = parseProgressEnvelope(envelopeAfterReset);

    const resetSave = EventsService.setEventUserData(
      mytaverseEventRef.current?.currentEventId ?? debugEventId,
      { assistantState: envelopeAfterReset },
    ).catch(() => {});
    saveProgressQueueRef.current = resetSave;
    await resetSave;
    log('[progress] resetProgress done, state:', progressState.current);
  }, []);

  return {
    progressState,
    saveProgressQueueRef,
    sessionProgressRef,
    cbOnCompletedStep,
    saveQuizAnswers,
    enqueueProgressSave,
    fetchParticipantProgress,
    resetProgress,
  };
};
